import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CatalogueItem } from '@/models/Catalogue';
import type { CatalogueProject, CatalogueProjectBom } from '@/models/Projects';
import {
  getBomItems,
  addBomItem,
  updateBomItem,
  removeBomItem,
} from '@/api/ProjectsApi';
import { getCatalogueItem } from '@/api/CatalogueApi';
import { useAlertsStore } from '@/store/AlertsStore';
import gettext from '@/i18n/gettext';

export interface BomItem {
  item: CatalogueItem;
  quantity: number;
  addedAt: Date;
  parentItemUuid?: string;
  bomItemUuid?: string;
}

export const useBomStore = defineStore('bom', () => {
  const { $gettext } = gettext;
  const selectedProject = ref<CatalogueProject | null>(null);
  const selectedBom = ref<CatalogueProjectBom | null>(null);
  const bomItems = ref<BomItem[]>([]);
  const isVisible = ref(false);
  const isLoading = ref(false);

  const alertsStore = useAlertsStore();

  // Save BOM data to localStorage
  const saveBomData = () => {
    try {
      localStorage.setItem(
        'bom-store',
        JSON.stringify({
          selectedProject: selectedProject.value,
          selectedBom: selectedBom.value,
          bomItems: bomItems.value,
        }),
      );
    } catch (error) {
      console.error('Failed to save BOM data to localStorage:', error);
    }
  };

  // Load items from selected BOM
  const loadSelectedBomItems = async () => {
    if (!selectedBom.value) {
      return;
    }
    try {
      isLoading.value = true;
      const serverItems = await getBomItems(selectedBom.value.uuid);
      const loadedItems: BomItem[] = [];

      await Promise.all(
        serverItems.map(async (serverItem) => {
          try {
            const itemDetails = await getCatalogueItem(serverItem.itemUuid);
            loadedItems.push({
              item: itemDetails,
              quantity: serverItem.quantity,
              addedAt: new Date(serverItem.createdAt),
              parentItemUuid: serverItem.parentItemUuid,
              bomItemUuid: serverItem.uuid,
            });
          } catch (innerError) {
            console.error(
              'Failed to load catalogue item for BOM item:',
              innerError,
            );
          }
        }),
      );
      // Replace items array to trigger reactivity; maintain stable sort by name to avoid flicker
      bomItems.value = loadedItems.sort((a, b) =>
        a.item.name.localeCompare(b.item.name),
      );
      saveBomData();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // If BOM items endpoint returns 404, clear the current BOM selection
      const status = error?.response?.status;
      if (status === 404) {
        clearSelection();
        alertsStore.info(
          $gettext('Selected BOM not found. Cleared current BOM selection.'),
        );
      } else {
        console.error('Failed to load BOM items:', error);
        alertsStore.error($gettext('Failed to load BOM items'));
      }
    } finally {
      isLoading.value = false;
    }
  };

  // Refresh BOM data (useful for manual refresh)
  const refreshBomData = async () => {
    if (selectedBom.value) {
      await loadSelectedBomItems();
    }
  };

  // Clear current BOM/project selection and items
  const clearSelection = () => {
    selectedProject.value = null;
    selectedBom.value = null;
    bomItems.value = [];
    saveBomData();
  };

  // Apply updated catalogue item details to items already in the BOM (no API call)
  const applyUpdatedItem = (updated: CatalogueItem) => {
    const index = bomItems.value.findIndex(
      (bi) => bi.item.uuid === updated.uuid,
    );
    if (index !== -1) {
      const existing = bomItems.value[index];
      bomItems.value[index] = {
        ...existing,
        item: {
          ...existing.item,
          ...updated,
        },
      };
      saveBomData();
    }
  };

  // Set selected project and BOM (async to load items)
  const setSelectedProjectAndBom = async (
    project: CatalogueProject,
    bom: CatalogueProjectBom,
  ) => {
    selectedProject.value = project;
    selectedBom.value = bom;
    bomItems.value = [];
    saveBomData();
    await loadSelectedBomItems();
    alertsStore.info(
      `${$gettext('Switched to BOM')}: ${bom.name} ${$gettext('in project')}: ${
        project.name
      }`,
    );
  };

  // Add item to BOM (sync with server)
  const addItem = async (item: CatalogueItem, quantity = 1) => {
    if (!selectedBom.value) {
      alertsStore.error($gettext('No BOM selected'));
      return;
    }

    const existingItem = bomItems.value.find(
      (bomItem) => bomItem.item.uuid === item.uuid,
    );

    if (existingItem) {
      try {
        const newQuantity = existingItem.quantity + quantity;
        await updateBomItem(selectedBom.value.uuid, item.uuid, {
          quantity: newQuantity,
        });
        existingItem.quantity = newQuantity;
        alertsStore.success(
          `${item.name}: +${quantity} (${newQuantity} ${$gettext('total')})`,
        );
      } catch (error) {
        console.error('Failed to update BOM item on server:', error);
        alertsStore.error($gettext('Failed to update BOM item on server'));
      }
    } else {
      try {
        const created = await addBomItem(selectedBom.value.uuid, {
          itemUuid: item.uuid,
          quantity,
        });
        bomItems.value.push({
          item,
          quantity: created.quantity,
          addedAt: new Date(created.createdAt),
          parentItemUuid: created.parentItemUuid,
          bomItemUuid: created.uuid,
        });
        alertsStore.success(`${item.name} ${$gettext('added to BOM')}`);
      } catch (error) {
        console.error('Failed to add item to BOM on server:', error);
        alertsStore.error($gettext('Failed to add item to BOM'));
      }
    }

    saveBomData();
  };

  // Remove item from BOM (sync with server)
  const removeItem = async (itemUuid: string) => {
    const index = bomItems.value.findIndex(
      (bomItem) => bomItem.item.uuid === itemUuid,
    );
    if (index !== -1) {
      const removedItem = bomItems.value[index];
      try {
        if (selectedBom.value) {
          await removeBomItem(selectedBom.value.uuid, itemUuid);
        }
        bomItems.value.splice(index, 1);
        saveBomData();
        alertsStore.success(
          `${$gettext('Removed from BOM')}: "${removedItem.item.name}"`,
        );
      } catch (error) {
        console.error('Failed to remove BOM item on server:', error);
        alertsStore.error($gettext('Failed to remove item from BOM'));
      }
    }
  };

  // Update item quantity (sync with server)
  const updateQuantity = async (itemUuid: string, quantity: number) => {
    const bomItem = bomItems.value.find((item) => item.item.uuid === itemUuid);
    if (bomItem) {
      if (quantity <= 0) {
        await removeItem(itemUuid);
      } else {
        try {
          if (selectedBom.value) {
            await updateBomItem(selectedBom.value.uuid, itemUuid, {
              quantity,
            });
          }
          bomItem.quantity = quantity;
          saveBomData();
        } catch (error) {
          console.error('Failed to update BOM item on server:', error);
          alertsStore.error($gettext('Failed to update item quantity'));
        }
      }
    }
  };

  // Toggle BOM dialog visibility
  const toggleBomDialog = () => {
    isVisible.value = !isVisible.value;
  };

  // Show BOM dialog
  const showBomDialog = () => {
    isVisible.value = true;
  };

  // Hide BOM dialog
  const hideBomDialog = () => {
    isVisible.value = false;
  };

  // Computed properties
  const totalItems = computed(() => {
    return bomItems.value.reduce(
      (total, bomItem) => total + bomItem.quantity,
      0,
    );
  });

  const totalCost = computed(() => {
    return bomItems.value.reduce((total, bomItem) => {
      return total + bomItem.item.cost * bomItem.quantity;
    }, 0);
  });

  const isEmpty = computed(() => {
    return bomItems.value.length === 0;
  });

  const hasSelectedProjectAndBom = computed(() => {
    return selectedProject.value !== null && selectedBom.value !== null;
  });

  const canEditSelectedBom = computed(() => {
    return selectedBom.value?.canEdit ?? false;
  });

  // Check if item is in BOM
  const isInBom = (itemUuid: string) => {
    return bomItems.value.some((bomItem) => bomItem.item.uuid === itemUuid);
  };

  // Get item quantity in BOM
  const getItemQuantity = (itemUuid: string) => {
    const bomItem = bomItems.value.find((item) => item.item.uuid === itemUuid);
    return bomItem ? bomItem.quantity : 0;
  };

  // Get child items of a parent
  const getChildItems = (parentItemUuid: string) => {
    return bomItems.value.filter(
      (item) => item.parentItemUuid === parentItemUuid,
    );
  };

  // Initialize BOM data from localStorage
  (async () => {
    try {
      const stored = localStorage.getItem('bom-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        selectedProject.value = parsed.selectedProject;
        selectedBom.value = parsed.selectedBom;

        if (parsed.selectedBom) {
          await loadSelectedBomItems();
        } else {
          bomItems.value =
            parsed.bomItems?.map((item: Record<string, unknown>) => ({
              ...item,
              addedAt: new Date(item.addedAt as string),
            })) || [];
        }
      }
    } catch (error) {
      console.error('Failed to load BOM data from localStorage:', error);
    }
  })();

  return {
    // State
    selectedProject,
    selectedBom,
    bomItems,
    isVisible,
    isLoading,

    // Computed
    totalItems,
    totalCost,
    isEmpty,
    hasSelectedProjectAndBom,
    canEditSelectedBom,

    // Actions
    setSelectedProjectAndBom,
    loadSelectedBomItems,
    refreshBomData,
    addItem,
    removeItem,
    updateQuantity,

    toggleBomDialog,
    showBomDialog,
    hideBomDialog,
    isInBom,
    getItemQuantity,
    getChildItems,
    saveBomData,
    clearSelection,
    applyUpdatedItem,
  };
});
